import { useEffect } from "react";
import { DialogStateLifecycleProps } from "./DialogStateInternalTypes";

/**
 * Hook to handle side effects and lifecycle events for dialog state management
 */
export function useDialogStateLifecycle({
  open,
  internalOpen,
  setInternalOpen,
  isSubmitting,
  externalForceKeepOpen,
  internalForceKeepOpen,
  setInternalForceKeepOpen,
  installationPhase,
  isExplicitlyClosed,
  onOpenChange,
  contentMountedRef,
  prevOpenRef,
  openStateChangeTimeoutRef,
  lastStateChangeTimestampRef,
  isInitialMountRef,
  installedRef
}: DialogStateLifecycleProps) {

  // Effect to reconcile internal and external state
  useEffect(() => {
    // Skip on initial mount as everything is already in sync
    if (isInitialMountRef.current) {
      return;
    }
    
    // Update internal state when external state changes
    if (prevOpenRef.current !== open) {
      prevOpenRef.current = open;
      
      // Only sync open state if it's been more than 100ms since internal change
      // This prevents loops when state is changed rapidly
      const timeSinceLastChange = Date.now() - lastStateChangeTimestampRef.current;
      if (timeSinceLastChange > 100) {
        setInternalOpen(open);
      }
    }
  }, [open, setInternalOpen]);

  // Effect to handle force keep open state
  useEffect(() => {
    if (externalForceKeepOpen !== internalForceKeepOpen) {
      setInternalForceKeepOpen(externalForceKeepOpen);
    }
  }, [externalForceKeepOpen, internalForceKeepOpen, setInternalForceKeepOpen]);

  // Setup effect to handle critical states
  useEffect(() => {
    if (isSubmitting || installationPhase) {
      // Ensure dialog is open in critical states
      if (!internalOpen) {
        onOpenChange(true);
        setInternalOpen(true);
      }
    }
  }, [isSubmitting, installationPhase, internalOpen, onOpenChange, setInternalOpen]);
  
  // Effect to clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (openStateChangeTimeoutRef.current) {
        clearTimeout(openStateChangeTimeoutRef.current);
        openStateChangeTimeoutRef.current = null;
      }
    };
  }, []);

  // Effect to track pixel installation completion
  useEffect(() => {
    if (installationPhase) {
      installedRef.current = true;
    }
  }, [installationPhase]);

  // Effect to mark content as mounted or unmounted
  useEffect(() => {
    contentMountedRef.current = true;
    
    return () => {
      contentMountedRef.current = false;
    };
  }, [contentMountedRef]);
  
  return null; // No render output from this hook
}
